// End-of-race screen: total time, penalties, best lap, stints and (for
// challenges) the medal, with buttons to go again, change setup or leave.

import { addTapHaptic } from '../input/haptics';
import type { RaceResult } from '../race/race';
import { COMPOUNDS } from '../race/tyres';
import { formatTime } from './hud';

export type Medal = 'gold' | 'silver' | 'bronze';

export interface ResultsInfo {
  title: string;
  subtitle: string;
  /** Previous best total for this race (undefined if none). */
  previousBest: number | undefined;
  medal?: Medal | undefined;
  /** Medal targets, for challenges. */
  targets?: Record<Medal, number>;
}

const MEDAL_LABEL: Record<Medal, string> = { gold: 'Gold', silver: 'Silver', bronze: 'Bronze' };

export class ResultsScreen {
  private readonly el: HTMLElement;
  private readonly body: HTMLElement;
  onAgain: (() => void) | undefined;
  onSetup: (() => void) | undefined;
  onMenu: (() => void) | undefined;

  constructor(root: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'overlay results';
    this.el.innerHTML = `<div class="results-body"></div>
      <div class="settings-footer">
        <button class="settings-btn secondary" data-a="menu">Menu</button>
        <button class="settings-btn secondary" data-a="setup">Change race</button>
        <button class="settings-btn primary" data-a="again">Race again</button>
      </div>`;
    root.appendChild(this.el);
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.body = this.el.querySelector('.results-body') as HTMLElement;
    for (const b of this.el.querySelectorAll<HTMLButtonElement>('button')) {
      b.addEventListener('click', () => {
        this.hide();
        const a = b.dataset['a'];
        if (a === 'again') this.onAgain?.();
        else if (a === 'setup') this.onSetup?.();
        else this.onMenu?.();
      });
      addTapHaptic(b);
    }
  }

  get open(): boolean {
    return this.el.classList.contains('show');
  }

  show(r: RaceResult, info: ResultsInfo): void {
    const newBest = info.previousBest === undefined || r.total < info.previousBest;
    const bestLine = newBest
      ? `<div class="results-pb">${info.previousBest === undefined ? 'First finish' : `New best by ${(info.previousBest - r.total).toFixed(3)} s`}</div>`
      : `<div class="results-prev">Best ${formatTime(info.previousBest)} (+${(r.total - info.previousBest!).toFixed(3)})</div>`;
    const medal = info.medal ? `<div class="results-medal ${info.medal}">${MEDAL_LABEL[info.medal]}</div>` : '';
    const targets = info.targets
      ? `<div class="results-targets">${(['gold', 'silver', 'bronze'] as Medal[]).map((m) => `<span class="${m}">${MEDAL_LABEL[m]} ${formatTime(info.targets![m])}</span>`).join('')}</div>`
      : '';
    const total = r.laps;
    const stints = r.stints
      .filter((s) => s.laps > 0)
      .map((s) => {
        const c = COMPOUNDS[s.compound];
        return `<span class="stint" style="flex:${s.laps};background:${c.color}" title="${c.name}">${c.letter} ${s.laps}</span>`;
      })
      .join('');
    const penalties = r.penalties > 0 ? `<div class="results-pen">Includes ${r.penalties} s of penalties${r.compoundRuleBroken ? ' (only one dry compound used: +30 s)' : ''}</div>` : '';
    this.body.innerHTML = `
      <div class="menu-title">${info.title}</div>
      <div class="results-sub">${info.subtitle}</div>
      <div class="results-time">${formatTime(r.total)}</div>
      ${medal}${bestLine}${penalties}
      <div class="results-stints">${stints}</div>
      <div class="results-stats"><span>Best lap <b>${formatTime(r.bestLap)}</b></span><span>Pit stops <b>${r.pitStops}</b></span><span>Laps <b>${total}</b></span></div>
      ${targets}`;
    this.el.classList.add('show');
  }

  hide(): void {
    this.el.classList.remove('show');
  }
}
